import type { Language } from './context.js';

const en = {
  access: {
    brandStatement: 'You are entering the desk. The markets are simulated; the decisions are yours.',
    tapeNq: 'NQ 20,000.25',
    tapeEs: 'ES 5,120.00',
    tapeZq: 'ZQ 95.8500',
    panelEyebrow: 'FREE PUBLIC ACCESS',
    panelTitle: 'Open your session',
    panelDescription: 'Your pseudonym gives you free access to your simulation workspace. No password or payment method is required.',
    pseudonymLabel: 'Pseudonym',
    pseudonymPlaceholder: 'E.g. Alpha_Desk',
    pseudonymLengthError: 'Enter a pseudonym between 3 and 24 characters.',
    pseudonymCharactersError: 'Use letters, numbers, spaces, hyphens, or underscores.',
    opening: 'Opening…',
    continue: 'Continue',
    progressSaved: 'Progress saved for your session',
    simulatedData: 'Simulated market data',
    privacy: 'Privacy',
    takeoverTitle: 'Session already controlled',
    takeoverBeforeAccount: 'The account ',
    takeoverAfterAccount: ' is open in another window. Taking over here will revoke control from that window.',
    cancel: 'Cancel',
    takeoverHere: 'Take over here'
  },
  intro: {
    dialogLabel: 'Head of Trading introduction',
    tagline: 'Liquidity leaves a footprint.',
    skip: 'Skip introduction'
  },
  missionHub: {
    accountNavigation: 'Account',
    trader: 'Trader',
    history: 'History',
    changePseudonym: 'Change pseudonym',
    eyebrow: 'DESK ASSIGNMENTS · NEW YORK',
    title: 'Choose your next mandate.',
    description: 'Three standalone episodes. One terminal. The market replays the exact same path so that only your execution changes.',
    viewportTitle: 'Screen too small for the terminal',
    viewportDescription: 'Your account and history remain available. Enlarge the window to at least 1,366 × 768 to launch a mission.',
    activeAttemptLabel: 'Active attempt',
    activeAttemptTitle: 'An attempt is in progress',
    activeAttemptDescription: 'Resume or abandon it before launching another mission.',
    abandon: 'Abandon',
    resume: 'Resume',
    availableMissions: 'Available missions',
    recommended: 'Recommended to start',
    duration: 'Duration',
    status: 'Status',
    alreadyPlayed: 'Already played',
    notPlayed: 'Not played',
    latest: 'Latest',
    best: 'Best',
    openBrief: 'Open brief',
    briefUnavailable: 'Brief unavailable from the server.',
    notAvailable: '—'
  },
  brief: {
    backToMissions: '← Missions',
    confidentialBrief: 'CONFIDENTIAL BRIEF',
    initialSituation: 'Starting position',
    knownMandate: 'Known mandate',
    window: 'Window',
    knownConstraints: 'Known constraints',
    assessment: 'Assessment',
    accessibleUniverse: 'Available universe',
    launchBlocked: 'Launch blocked',
    terminalMinimum: 'The terminal requires a minimum resolution of 1,366 × 768.',
    marketStarts: 'The market starts as soon as the terminal opens. Future information will only be communicated through the feed.',
    preparingDesk: 'Preparing the desk…',
    launchMission: 'Launch mission',
    easternTime: 'ET'
  },
  history: {
    navigation: 'History navigation',
    backToMissions: '← Missions',
    eyebrow: 'TRADER RECORD',
    titleBeforeName: 'History for ',
    description: 'Every attempt is retained, including abandoned attempts and attempts from earlier versions.',
    noAttempts: 'No attempts yet',
    firstMandate: 'Your first mandate will appear here.',
    mission: 'Mission',
    date: 'Date',
    duration: 'Duration',
    status: 'Status',
    version: 'Version',
    score: 'Score',
    earlierVersion: 'Earlier version',
    open: 'View',
    minutes: 'min',
    notAvailable: '—'
  },
  debrief: {
    navigation: 'Debrief navigation',
    history: 'History',
    missions: 'Missions',
    mandateHandled: 'Mandate completed.',
    technicalIncident: 'Technical incident.',
    deskExpectedMore: 'The desk expected better.',
    scenario: 'Scenario',
    noScore: 'No score',
    appliedCaps: 'Applied caps',
    scoreCap: 'Score ≤',
    easternTime: 'ET',
    deskAssessment: 'Desk assessment',
    scoreBreakdown: 'Score breakdown',
    dimension: 'Dimension',
    value: 'Value',
    reference: 'Reference',
    subscore: 'Subscore',
    weight: 'Weight',
    contribution: 'Contribution',
    benchmarks: 'Benchmarks',
    comparison: 'Comparison',
    previousAttempt: 'Previous attempt',
    difference: 'Difference',
    timeline: 'Timeline',
    executions: 'Executions',
    hideFills: 'Hide fills',
    showAllFills: 'Show all fills',
    time: 'Time',
    instrument: 'Instrument',
    side: 'Side',
    quantity: 'Qty',
    price: 'Price',
    slippage: 'Slippage',
    simulatedCommission: 'Simulated commission',
    buy: 'Buy',
    sell: 'Sell',
    ticks: 'ticks',
    backToMissions: 'Back to missions',
    fullHistory: 'Full history',
    replayMarket: 'Replay the same market',
    notAvailable: '—'
  }
} as const;

type CopyShape<T> = {
  readonly [Key in keyof T]: T[Key] extends string
    ? string
    : T[Key] extends Readonly<Record<string, unknown>>
      ? CopyShape<T[Key]>
      : T[Key];
};

const fr: CopyShape<typeof en> = {
  access: {
    brandStatement: 'Vous entrez au desk. Les marchés sont simulés, les décisions vous appartiennent.',
    tapeNq: 'NQ 20 000,25',
    tapeEs: 'ES 5 120,00',
    tapeZq: 'ZQ 95,8500',
    panelEyebrow: 'ACCÈS PUBLIC GRATUIT',
    panelTitle: 'Ouvrir votre session',
    panelDescription: 'Votre pseudo ouvre gratuitement votre espace de simulation. Aucun mot de passe ni moyen de paiement n’est requis.',
    pseudonymLabel: 'Pseudo',
    pseudonymPlaceholder: 'Ex. Alpha_Desk',
    pseudonymLengthError: 'Saisissez un pseudo de 3 à 24 caractères.',
    pseudonymCharactersError: 'Utilisez des lettres, chiffres, espaces, tirets ou underscores.',
    opening: 'Ouverture…',
    continue: 'Continuer',
    progressSaved: 'Progression conservée pour votre session',
    simulatedData: 'Données de marché simulées',
    privacy: 'Confidentialité',
    takeoverTitle: 'Session déjà contrôlée',
    takeoverBeforeAccount: 'Le compte ',
    takeoverAfterAccount: ' est ouvert dans une autre fenêtre. Reprendre ici déconnectera son contrôle.',
    cancel: 'Annuler',
    takeoverHere: 'Reprendre ici'
  },
  intro: {
    dialogLabel: 'Introduction Head of Trading',
    tagline: 'La liquidité laisse une empreinte.',
    skip: 'Passer l’introduction'
  },
  missionHub: {
    accountNavigation: 'Compte',
    trader: 'Trader',
    history: 'Historique',
    changePseudonym: 'Changer de pseudo',
    eyebrow: 'MANDATS DU DESK · NEW YORK',
    title: 'Choisissez votre prochain mandat.',
    description: 'Trois épisodes indépendants. Un même terminal. Le marché rejoue exactement le même chemin pour que seule votre exécution change.',
    viewportTitle: 'Écran insuffisant pour le terminal',
    viewportDescription: 'Le compte et l’historique restent accessibles. Agrandissez la fenêtre à 1 366 × 768 au minimum pour lancer une mission.',
    activeAttemptLabel: 'Tentative active',
    activeAttemptTitle: 'Une tentative est en cours',
    activeAttemptDescription: 'Reprenez-la ou abandonnez-la avant de lancer une autre mission.',
    abandon: 'Abandonner',
    resume: 'Reprendre',
    availableMissions: 'Missions disponibles',
    recommended: 'Recommandée pour commencer',
    duration: 'Durée',
    status: 'Statut',
    alreadyPlayed: 'Déjà jouée',
    notPlayed: 'Non jouée',
    latest: 'Dernier',
    best: 'Meilleur',
    openBrief: 'Ouvrir le brief',
    briefUnavailable: 'Brief indisponible côté serveur.',
    notAvailable: '—'
  },
  brief: {
    backToMissions: '← Missions',
    confidentialBrief: 'BRIEF CONFIDENTIEL',
    initialSituation: 'Situation de départ',
    knownMandate: 'Mandat connu',
    window: 'Fenêtre',
    knownConstraints: 'Contraintes connues',
    assessment: 'Évaluation',
    accessibleUniverse: 'Univers accessible',
    launchBlocked: 'Lancement bloqué',
    terminalMinimum: 'Le terminal requiert au minimum 1 366 × 768.',
    marketStarts: 'Le marché commence dès l’ouverture du terminal. Les informations futures ne seront communiquées que dans le flux.',
    preparingDesk: 'Préparation du desk…',
    launchMission: 'Lancer la mission',
    easternTime: 'ET'
  },
  history: {
    navigation: 'Navigation de l’historique',
    backToMissions: '← Missions',
    eyebrow: 'DOSSIER TRADER',
    titleBeforeName: 'Historique de ',
    description: 'Chaque tentative est conservée, y compris les abandons et les versions antérieures.',
    noAttempts: 'Aucune tentative',
    firstMandate: 'Votre premier mandat apparaîtra ici.',
    mission: 'Mission',
    date: 'Date',
    duration: 'Durée',
    status: 'Statut',
    version: 'Version',
    score: 'Score',
    earlierVersion: 'Version antérieure',
    open: 'Consulter',
    minutes: 'min',
    notAvailable: '—'
  },
  debrief: {
    navigation: 'Navigation du débrief',
    history: 'Historique',
    missions: 'Missions',
    mandateHandled: 'Mandat traité.',
    technicalIncident: 'Incident technique.',
    deskExpectedMore: 'Le desk attendait mieux.',
    scenario: 'Scénario',
    noScore: 'Aucun score',
    appliedCaps: 'Plafonds appliqués',
    scoreCap: 'Score ≤',
    easternTime: 'ET',
    deskAssessment: 'Lecture du desk',
    scoreBreakdown: 'Décomposition du score',
    dimension: 'Dimension',
    value: 'Valeur',
    reference: 'Référence',
    subscore: 'Sous-score',
    weight: 'Poids',
    contribution: 'Contribution',
    benchmarks: 'Repères',
    comparison: 'Comparaison',
    previousAttempt: 'Tentative précédente',
    difference: 'Écart',
    timeline: 'Chronologie',
    executions: 'Exécutions',
    hideFills: 'Masquer les fills',
    showAllFills: 'Afficher tous les fills',
    time: 'Heure',
    instrument: 'Instrument',
    side: 'Sens',
    quantity: 'Qté',
    price: 'Prix',
    slippage: 'Slippage',
    simulatedCommission: 'Commission simulée',
    buy: 'Acheter',
    sell: 'Vendre',
    ticks: 'ticks',
    backToMissions: 'Retour aux missions',
    fullHistory: 'Historique complet',
    replayMarket: 'Rejouer le même marché',
    notAvailable: '—'
  }
};

export const screensCopy: Readonly<Record<Language, CopyShape<typeof en>>> = { en, fr };
